import type { MetricWeek } from '@/hooks/useMetricWeeks'

export type WeeklyPlanningStatus = 'planned' | 'unset' | 'excluded'

export interface WeeklyPlanEntry {
  status?: WeeklyPlanningStatus
  leads?: number | null
  spend?: number | null
}

export type WeeklyPlanDraft = Record<string, Record<number, WeeklyPlanEntry>>

export interface EffectiveWeeklyPlan {
  status: WeeklyPlanningStatus
  leads: number | null
  spend: number | null
}

export interface PartnerMarketingWeeklyPlanRow {
  partner_number: number
  week_id: number
  service: string
  account_id: number
  planning_status: WeeklyPlanningStatus
  leads_plan: number | null
  spend_plan: number | null
  updated_at?: string | null
  updated_by_user_id?: string | null
}

export function getPartnerMarketingTargetKey(service: string, accountId: number): string {
  return `${service}:${accountId}`
}

export function buildDraftFromWeeklyPlanRows(
  rows: PartnerMarketingWeeklyPlanRow[],
  weeks: MetricWeek[]
): WeeklyPlanDraft {
  const weekNumberById = new Map(weeks.map((week) => [week.week_id, week.week_number_in_period]))
  const draft: WeeklyPlanDraft = {}

  rows.forEach((row) => {
    const weekNumber = weekNumberById.get(row.week_id)
    if (!weekNumber) return

    const targetKey = getPartnerMarketingTargetKey(row.service, row.account_id)
    const targetDraft = draft[targetKey] || {}

    targetDraft[weekNumber] = {
      status: row.planning_status,
      leads: row.leads_plan,
      spend: row.spend_plan
    }

    draft[targetKey] = targetDraft
  })

  return draft
}

export function flattenDraftToWeeklyPlanRows(
  draft: WeeklyPlanDraft,
  weeks: MetricWeek[],
  partnerNumber: number,
  updatedByUserId: string | null
): PartnerMarketingWeeklyPlanRow[] {
  const weekIdByNumber = new Map(weeks.map((week) => [week.week_number_in_period, week.week_id]))
  const items: PartnerMarketingWeeklyPlanRow[] = []

  Object.keys(draft).forEach((targetKey) => {
    const separatorIndex = targetKey.lastIndexOf(':')
    if (separatorIndex <= 0) return

    const service = targetKey.slice(0, separatorIndex)
    const accountId = Number(targetKey.slice(separatorIndex + 1))
    if (!service || !Number.isFinite(accountId)) return

    weeks.forEach((week) => {
      const weekId = weekIdByNumber.get(week.week_number_in_period)
      if (!weekId || weekId < 0) return

      // The planner intentionally supports inheritance: a value entered on an
      // earlier week is displayed on later weeks until it is overridden. The
      // database, however, is keyed by week_id, so persist the effective value
      // for every week instead of only persisting explicit editor changes.
      const effective = getEffectiveWeeklyPlan(
        targetKey,
        week.week_number_in_period,
        draft,
        weeks
      )

      items.push({
        partner_number: partnerNumber,
        week_id: weekId,
        service,
        account_id: accountId,
        planning_status: effective.status,
        leads_plan: effective.status === 'planned' ? effective.leads : null,
        spend_plan: effective.status === 'planned' ? effective.spend : null,
        updated_by_user_id: updatedByUserId,
        updated_at: null
      })
    })
  })

  return items
}

export function getEffectiveWeeklyPlan(
  targetKey: string,
  weekNumber: number,
  source: WeeklyPlanDraft,
  weeks: MetricWeek[]
): EffectiveWeeklyPlan {
  const effective: EffectiveWeeklyPlan = {
    status: 'unset',
    leads: null,
    spend: null
  }

  const targetDraft = source[targetKey] || {}

  weeks
    .filter((week) => week.week_number_in_period <= weekNumber)
    .sort((a, b) => a.week_number_in_period - b.week_number_in_period)
    .forEach((week) => {
      const explicit = targetDraft[week.week_number_in_period]
      if (!explicit) return

      if (explicit.status !== undefined) {
        effective.status = explicit.status
      }
      if (explicit.leads !== undefined) {
        effective.leads = explicit.leads
      }
      if (explicit.spend !== undefined) {
        effective.spend = explicit.spend
      }
    })

  return effective
}
